import React, { useEffect, useRef, useState } from 'react'
import debounce from 'lodash-es/debounce'

import { useSession, useTenant } from '@rewardops/auth'
import { Icons, Sheet, SheetContent, SheetTitle, Spinner } from '@rewardops/ui'
import { AnalyticEvents } from '@rewardops/utils'

import {
  calculateCartPointsCost,
  hasCartLimitExceeded,
} from '~/utils/cart-utils'
import { getDeviceType } from '~/utils/device'
import { extractItemDetails } from '~/utils/item-utils'
import { useAnalytics } from '~/components/hooks/use-analytics'
import { useApi } from '~/components/hooks/use-api'
import { useCartActions } from '~/components/hooks/use-cart-actions'
import { useFormat } from '~/components/hooks/use-format'
import MapleButton from '~/components/maple/components/button'
import { useShoppingCartError } from '~/components/maple/pages/error-messages/shopping-cart-error'
import { QuantitySelector } from '~/components/maple/pages/item/quantity-selector'
import { useTranslation } from '~/app/i18n/client'
import LimitExceededModal from '../../components/limit-exceeded-modal'

type CartPopupType = {
  shouldOpen: boolean
  onOpenChange: (open: boolean) => void
}

export const CartPopup: React.FC<CartPopupType> = ({
  shouldOpen,
  onOpenChange,
}: CartPopupType) => {
  const { t } = useTranslation()
  const { data: session } = useSession()
  const [isUpdatingCart, setIsUpdatingCart] = useState(false)
  let { cartItems, isLoadingCartItems, refetchCartItems } = useCartActions()
  const analytics = useAnalytics()
  const tenant = useTenant()

  useEffect(() => {
    if (session) {
      refetchCartItems()
    }
  }, [])

  // Check if all cart items have supplier_items (not optimistically added)
  const hasValidCartItems = cartItems?.cartItems?.every(
    (item: any) => item?.supplier_items?.length > 0,
  )

  useEffect(() => {
    if (
      shouldOpen &&
      !isLoadingCartItems &&
      cartItems?.cartItems?.length &&
      hasValidCartItems
    ) {
      const params = {
        type: AnalyticEvents.ViewCart,
        data: {
          currency: tenant?.defaultCurrency,
          value: calculateCartPointsCost(
            cartItems?.cartItems,
            tenant?.defaultPoints,
          ),
          items: cartItems?.cartItems?.map((item: any) => ({
            item_id: item?.brands?.[0]?.name,
            item_name: item?.name,
            price: item?.prices?.sale_price?.[tenant?.defaultPoints],
            quantity: item?.quantity,
            category: item?.categories?.[0]?.path,
          })),
          member_id: session?.user?.member?.id,
          source: 'add_to_cart_popup',
          device_type: getDeviceType(),
          popup_item_count: cartItems?.cartItems?.length,
          source_page: window?.location?.pathname,
        },
      }

      analytics.sendCustomGoogleAnalyticsEvent(params)
    }
  }, [shouldOpen, isLoadingCartItems, cartItems?.cartItems, hasValidCartItems])

  const onRemoveFromCart = (item: any) => {
    const params = {
      type: AnalyticEvents.CartPopupRemoveFromCart,
      data: {
        currency: tenant?.defaultCurrency,
        value: item?.prices?.sale_price?.[tenant?.defaultPoints],
        items: [
          {
            item_id: item?.brands?.[0]?.name,
            item_name: item?.name,
            price: item?.prices?.sale_price?.[tenant?.defaultPoints],
            quantity: item?.quantity,
            category: item?.categories?.[0]?.path,
          },
        ],
        member_id: session?.user?.member?.id,
        device_type: getDeviceType(),
        popup_item_count: cartItems?.cartItems?.length - 1,
        source_page: window?.location?.pathname,
      },
    }

    analytics.sendCustomGoogleAnalyticsEvent(params)
  }

  const items = cartItems?.cartItems

  if (!items) return null

  const totalQuantity = items.reduce(
    (acc: number, item: any) => acc + item.quantity,
    0,
  )

  return (
    <Sheet open={shouldOpen} onOpenChange={onOpenChange}>
      <SheetContent className="flex h-full w-full flex-col px-0">
        <SheetTitle />
        <div className="flex h-full flex-col">
          <h1
            className="border-b px-4 pb-4 text-lg font-bold"
            id="cart-popup-title"
          >
            {t('shoppingCart.title')} ({totalQuantity})
          </h1>
          {isLoadingCartItems ? (
            <Loading />
          ) : (
            <div className="relative flex-grow overflow-y-auto">
              <div
                className={`${
                  isUpdatingCart ? 'pointer-events-none opacity-50' : ''
                } mt-4 transition-opacity duration-300`}
              >
                {items.map((item: any, index: number) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    isUpdatingCart={isUpdatingCart}
                    setIsUpdatingCart={setIsUpdatingCart}
                    onRemoveFromCart={onRemoveFromCart}
                    id={`cart-popup-item-${index}`}
                  />
                ))}
              </div>
              {isUpdatingCart && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50">
                  <Loading />
                </div>
              )}
            </div>
          )}
          <div className="mt-auto flex w-full flex-col gap-2 border-t p-4 px-4">
            <MapleButton
              id="view-cart"
              link="/shopping-cart"
              size="default"
              buttonType="link"
              className="w-full py-2 text-white"
            >
              {t('shoppingCart.viewCart')}
            </MapleButton>
            <MapleButton
              id="continue-shopping"
              onClick={() => onOpenChange(false)}
              size="default"
              buttonStyle="secondary"
              buttonType="button"
              className="mt-2 w-full py-2"
            >
              {t('shoppingCart.continueShopping')}
            </MapleButton>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

const CartItem = ({
  item,
  isUpdatingCart,
  setIsUpdatingCart,
  id,
  onRemoveFromCart,
}: {
  item: any
  isUpdatingCart: boolean
  setIsUpdatingCart: any
  id: string
  onRemoveFromCart?: (item: any) => void
}) => {
  const { t } = useTranslation()
  const { tenant } = useApi()
  const { cartItems, setItemQuantity, removeItem } = useCartActions()
  const [localQuantity, setLocalQuantity] = useState(item.quantity || 1)
  const { formatNumber } = useFormat()
  const { showShoppingCartError } = useShoppingCartError()
  const extractItem = extractItemDetails(item, tenant.defaultPoints)
  const [limitExceededMessage, setLimitExceededMessage] = useState('')

  const { regularPrice, salePrice, orderToken } = extractItem
  const actualPrice = salePrice != regularPrice ? salePrice : regularPrice

  const hasMounted = useRef(false)

  // These use effects are necessary to avoid calling setLocalQuantity()
  // on first render

  useEffect(() => {
    if (item.quantity !== localQuantity) {
      setLocalQuantity(item.quantity)
    }
  }, [item.quantity])

  useEffect(() => {
    if (hasMounted.current && localQuantity !== item.quantity) {
      debouncedSetItemQuantity()
    } else {
      hasMounted.current = true
    }
    return () => debouncedSetItemQuantity.cancel()
  }, [localQuantity, item.quantity])

  const debouncedSetItemQuantity = debounce(async () => {
    setIsUpdatingCart(true)
    try {
      await setItemQuantity({ orderToken }, localQuantity)
    } catch (error) {
      showShoppingCartError()
      setLocalQuantity(item.quantity)
    } finally {
      // Reset to original quantity on error
      setIsUpdatingCart(false)
    }
  }, 500)

  const handleRemoveItem = async () => {
    setIsUpdatingCart(true)
    await removeItem(item.id, orderToken)
      .catch(() => showShoppingCartError())
      .finally(() => {
        setIsUpdatingCart(false)

        if (onRemoveFromCart) {
          onRemoveFromCart(item)
        }
      })
  }

  const handleQuantityChange = (newQuantity: number) => {
    if (!isUpdatingCart) {
      if (
        hasCartLimitExceeded(
          cartItems.items,
          { sivId: item?.supplier_items[0].id, quantity: newQuantity },
          cartItems.cart_quantity_limit,
        )
      ) {
        setLimitExceededMessage(t('cartLimitExceeded'))
        return
      }
      setLocalQuantity(newQuantity)
    }
  }

  return (
    <div className="mb-4 flex flex-row border-b px-4 pb-4" id={id}>
      <div
        id="item-image"
        className="flex h-[64px] w-[64px] shrink-0 items-center justify-center"
      >
        <img
          src={item.images[0]?.url}
          alt={item.name}
          className="mr-4 max-h-full max-w-full object-contain"
        />
      </div>
      <div className="flex-grow">
        <div className="flex flex-col gap-3">
          <h2>{item.name}</h2>
          <div>
            <PricingDetails
              classNames={'flex flex-row items-center gap-2'}
              regularPrice={regularPrice}
              salePrice={salePrice}
            />
          </div>
          <LimitExceededModal
            shouldTrigger={limitExceededMessage !== ''}
            onClose={() => setLimitExceededMessage('')}
            asChild
            limitExceededMessage={limitExceededMessage}
          >
            <div className="flex items-center justify-between">
              <QuantitySelector
                quantity={localQuantity}
                onQuantityChange={handleQuantityChange}
                className="w-full"
              />
              <button
                className="ml-2 cursor-pointer"
                onClick={handleRemoveItem}
                aria-label="Remove item"
              >
                <Icons.trash2 />
              </button>
            </div>
          </LimitExceededModal>
          <div>
            <div className="">{t('shoppingCart.subtotal')}</div>
            <div className="text-xl font-semibold" id="shopping-cart-subtotal">
              {formatNumber(parseInt(actualPrice) * localQuantity)} pts
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

type PricingDetailsType = {
  classNames?: string
  regularPrice: number
  salePrice: number
}

export const PricingDetails: React.FC<PricingDetailsType> = ({
  classNames = '',
  regularPrice,
  salePrice,
}: PricingDetailsType) => {
  const { formatNumber } = useFormat()
  const tenant = useTenant()
  const isOnSale = salePrice && salePrice != regularPrice

  const saleColor = tenant.styleVariant?.pricingDetails?.saleColor

  if (isOnSale) {
    return (
      <div className={`${classNames}`} id="sale-pricing-details">
        <p
          className={'text-lg leading-3'}
          style={{
            color: saleColor,
          }}
          id="sale-price"
        >
          {formatNumber(salePrice, 0)} pts
        </p>
        <p className="text-sm font-normal line-through" id="sale-regular-price">
          {formatNumber(regularPrice, 0)} pts
        </p>
      </div>
    )
  }

  return (
    <div className={`${classNames}`} id="regular-pricing-details">
      {formatNumber(regularPrice, 0)} pts
    </div>
  )
}

const Loading = () => <Spinner className="mx-auto" />
